/**
 * The kinds of account a person can connect (Features › Connections › Your accounts): git hosting, package registries
 * and tools. The server's catalog (GET /api/v1/me/accounts → `catalog`) is the list Flow offers; this file adds the
 * help a person needs to make a token (where, least privilege, copyable scopes). A type the catalog sends and this
 * file does not know still works, with the catalog's own label and fields (`typeDef`).
 */

export const CATEGORIES = [
  { key: 'git', label: 'Git hosting' },
  { key: 'registry', label: 'Package registries' },
  { key: 'tool', label: 'Tools' },
];

export const USED_FOR = {
  git: 'Clone private repositories, pull and push from Flow, its agents and your AI tool.',
  registry: 'npm install / pip / mvn / crane in your sandbox: the token is written to a config file for that one command and removed after.',
  tool: "For agents' tools; connecting only for now.",
};

const SERVER_URL = 'Server URL';
const REGISTRY_URL = 'Registry URL';

export const ACCOUNT_TYPES = {
  github: {
    key: 'github',
    label: 'GitHub',
    category: 'git',
    oauth: true,
    defaultBaseUrl: 'https://github.com',
    baseUrlLabel: SERVER_URL,
    baseUrlPlaceholder: 'https://github.com',
    baseUrlHelp: 'Leave empty for github.com; your GitHub Enterprise Server address otherwise.',
    tokenFields: ['token'],
    tokenPlaceholder: 'ghp_… or github_pat_…',
    where: [
      'GitHub › your avatar › Settings › Developer settings › Personal access tokens.',
      'Tokens (classic): Generate new token (classic), tick only `repo`, set an expiry.',
      'Or Fine-grained tokens: choose the repositories, then Contents: Read and write (Metadata: Read is added).',
      'Copy the token once and paste it here: GitHub never shows it again, and neither does Flow.',
    ],
    grant: 'Flow only clones, pulls and pushes: `repo` (classic) or Contents read/write on chosen repositories (fine-grained). Nothing else is needed. Connect with OAuth asks GitHub for `repo` only.',
    commands: 'Classic token scopes:\n  repo\n\nFine-grained token:\n  Repository access: Only select repositories\n  Repository permissions:\n    Contents: Read and write\n    Metadata: Read-only',
  },
  gitlab: {
    key: 'gitlab',
    label: 'GitLab',
    category: 'git',
    oauth: true,
    defaultBaseUrl: 'https://gitlab.com',
    baseUrlLabel: SERVER_URL,
    baseUrlPlaceholder: 'https://gitlab.com or https://git.tools.mia-platform.eu',
    baseUrlHelp: 'gitlab.com or your self-managed GitLab, e.g. https://git.tools.mia-platform.eu',
    tokenFields: ['token'],
    tokenPlaceholder: 'glpat-…',
    where: [
      'GitLab › your avatar › Edit profile › Access tokens (<host>/-/user_settings/personal_access_tokens).',
      'Add new token: a name, an expiry, and the scopes read_repository and write_repository (read_user is optional).',
      'Create, copy the token once and paste it here.',
    ],
    grant: 'Flow only clones, pulls and pushes over HTTPS: read_repository + write_repository. `api` is more than it needs. Connect with OAuth asks for read_user, read_repository and write_repository.',
    commands: 'Personal access token scopes:\n  read_repository\n  write_repository\n  read_user        (optional: shows your name)',
  },
  bitbucket: {
    key: 'bitbucket',
    label: 'Bitbucket Cloud',
    category: 'git',
    oauth: true,
    fixedBaseUrl: 'https://bitbucket.org',
    defaultBaseUrl: 'https://bitbucket.org',
    baseUrlLabel: SERVER_URL,
    tokenFields: ['token'],
    tokenPlaceholder: 'ATCTT3x…',
    where: [
      'One repository: Bitbucket › the repository › Repository settings › Security › Access tokens › Create Repository Access Token.',
      'Several repositories: Workspace settings › Security › Access tokens (workspace access tokens, Premium) or Project settings › Access tokens.',
      'Scopes: Repositories Read and Write. Copy the token once and paste it here.',
    ],
    grant: 'Flow only clones, pulls and pushes: Repositories Read + Write. Admin, pull request and pipeline scopes are not needed. Connect with OAuth asks for account, repository and repository:write.',
    commands: 'Access token scopes:\n  repository\n  repository:write',
  },
  'bitbucket-dc': {
    key: 'bitbucket-dc',
    label: 'Bitbucket Data Center',
    category: 'git',
    oauth: true,
    baseUrlRequired: true,
    baseUrlLabel: SERVER_URL,
    baseUrlPlaceholder: 'https://bitbucket.example.com',
    baseUrlHelp: 'Your Bitbucket Data Center (Server) address.',
    tokenFields: ['username', 'token'],
    usernameLabel: 'Bitbucket username',
    tokenPlaceholder: 'HTTP access token',
    where: [
      'Bitbucket › your avatar › Manage account › HTTP access tokens (<base>/plugins/servlet/access-tokens/manage).',
      'Create token: Project permissions Read, Repository permissions Write, and an expiry.',
      'Copy the token once; the username is your Bitbucket login.',
    ],
    grant: 'Flow only clones, pulls and pushes: Repository Write (includes read). Project or admin write is not needed. Connect with OAuth asks for REPO_WRITE.',
    commands: 'HTTP access token permissions:\n  Project permissions: Read\n  Repository permissions: Write',
  },
  'azure-devops': {
    key: 'azure-devops',
    label: 'Azure DevOps',
    category: 'git',
    oauth: true,
    baseUrlRequired: true,
    baseUrlLabel: SERVER_URL,
    baseUrlPlaceholder: 'https://dev.azure.com/your-org',
    baseUrlHelp: 'Your organization: https://dev.azure.com/<org>.',
    tokenFields: ['token'],
    tokenPlaceholder: 'Personal access token',
    hiddenOptions: ['tenant'],
    where: [
      'https://dev.azure.com/<org> › User settings (the person icon, top right) › Personal access tokens › New Token.',
      'Organization: this one only; Scopes: Custom defined › Code: Read & write; set an expiry.',
      'Create, copy the token once and paste it here.',
    ],
    grant: 'Flow only clones, pulls and pushes: Code Read & write. Full access is more than it needs. Connect with OAuth (Entra ID) asks for vso.code_write.',
    commands: 'Personal access token scopes:\n  Code: Read & write   (vso.code_write)',
  },
  gitea: {
    key: 'gitea',
    label: 'Gitea / Forgejo',
    category: 'git',
    oauth: true,
    baseUrlRequired: true,
    baseUrlLabel: SERVER_URL,
    baseUrlPlaceholder: 'https://gitea.example.com',
    baseUrlHelp: 'Your Gitea, Forgejo or Codeberg address.',
    tokenFields: ['token'],
    tokenPlaceholder: 'Access token',
    where: [
      'Your avatar › Settings › Applications › Manage access tokens (<base>/user/settings/applications).',
      'Generate token: a name, repository Read and write, user Read.',
      'Copy the token once and paste it here.',
    ],
    grant: 'Flow only clones, pulls and pushes: write:repository (+ read:user for your name). Admin or organization scopes are not needed.',
    commands: 'Token permissions:\n  repository: Read and write   (write:repository)\n  user: Read                   (read:user)',
  },
  npm: {
    key: 'npm',
    label: 'npm registry',
    category: 'registry',
    oauth: false,
    baseUrlRequired: true,
    baseUrlLabel: REGISTRY_URL,
    baseUrlPlaceholder: 'https://registry.npmjs.org/',
    baseUrlHelp: 'npmjs: https://registry.npmjs.org/ · GitHub Packages: https://npm.pkg.github.com · Nexus/Artifactory: the npm repository URL, e.g. https://nexus.example.com/repository/npm-group/',
    tokenFields: ['token'],
    optionalUsername: true,
    usernameLabel: 'Username (optional: basic auth, e.g. Nexus)',
    tokenPlaceholder: 'npm_… or your registry token',
    options: [
      { key: 'scope', label: 'Scope', kind: 'string', default: '' },
      { key: 'useAsDefault', label: 'Use as default registry', kind: 'bool', default: false },
    ],
    optionHelp: {
      scope: 'Optional, e.g. @acme: packages of that scope come from this registry (@acme:registry=).',
      useAsDefault: 'Every package comes from this registry (registry=).',
    },
    where: [
      'npmjs.com: run `npm token create --read-only`, or avatar › Access Tokens › Generate New Token › Granular Access Token with Packages and scopes: Read-only.',
      'Nexus: your user › User Token › Access user token (name + passcode: put the name as username); Artifactory: Edit Profile › Generate an Identity Token.',
      'GitHub Packages (https://npm.pkg.github.com): a classic personal access token with read:packages; fine-grained tokens do not work with it.',
    ],
    grant: 'Flow only installs: a read-only token is enough. Publish rights are not needed.',
    commands: 'npm token create --read-only\n\nGitHub Packages, classic token scopes:\n  read:packages',
  },
  pypi: {
    key: 'pypi',
    label: 'PyPI-compatible index',
    category: 'registry',
    oauth: false,
    baseUrlRequired: true,
    baseUrlLabel: REGISTRY_URL,
    baseUrlPlaceholder: 'https://pypi.org/simple/',
    baseUrlHelp: 'The /simple index URL, e.g. https://nexus.example.com/repository/pypi-group/simple/',
    tokenFields: ['username', 'token'],
    usernameLabel: 'Username',
    defaultUsername: '__token__',
    tokenLabel: 'Token or password',
    tokenPlaceholder: 'pypi-…',
    options: [{ key: 'useAsDefault', label: 'Use as default index', kind: 'bool', default: false }],
    optionHelp: { useAsDefault: 'pip uses it as index-url instead of extra-index-url.' },
    where: [
      'PyPI.org: Account settings › API tokens › Add API token; the username is __token__ and the token starts with pypi-.',
      'Nexus / Artifactory / devpi: your user token (or password) for that index, with your username.',
      'GitLab package registry: a personal access token with read_api; username = your GitLab username.',
    ],
    grant: 'Flow only installs: read access to the index. Upload rights are not needed.',
    commands: 'PyPI.org API token:\n  username: __token__\n  password: pypi-…\n\nGitLab token scopes:\n  read_api',
  },
  maven: {
    key: 'maven',
    label: 'Maven repository',
    category: 'registry',
    oauth: false,
    baseUrlRequired: true,
    baseUrlLabel: REGISTRY_URL,
    baseUrlPlaceholder: 'https://nexus.example.com/repository/maven-public/',
    baseUrlHelp: 'The repository URL, e.g. Nexus …/repository/maven-public/ or https://maven.pkg.github.com/<owner>/<repo>',
    tokenFields: ['username', 'token'],
    usernameLabel: 'Username',
    tokenLabel: 'Token or password',
    tokenPlaceholder: 'User token passcode',
    options: [
      { key: 'serverId', label: 'Server id', kind: 'string', default: '' },
      { key: 'useAsDefault', label: 'Use as default repository', kind: 'bool', default: false },
    ],
    optionHelp: {
      serverId: 'The <server><id> in settings.xml, matching the repository id in your pom.xml. Empty: flow-<host>.',
      useAsDefault: 'A mirror of every repository (mirrorOf *).',
    },
    where: [
      'Nexus: your user › User Token › Access user token: the name is the username, the passcode the token.',
      'Artifactory: Edit Profile › Generate an Identity Token; the username is your login.',
      'GitHub Packages: your GitHub login and a classic personal access token with read:packages.',
    ],
    grant: 'Flow only resolves dependencies: read access to the repository. Deploy rights are not needed.',
    commands: 'GitHub Packages, classic token scopes:\n  read:packages',
  },
  oci: {
    key: 'oci',
    label: 'Docker / OCI registry',
    category: 'registry',
    oauth: false,
    baseUrlRequired: true,
    baseUrlLabel: REGISTRY_URL,
    baseUrlPlaceholder: 'https://ghcr.io',
    baseUrlHelp: 'Docker Hub: https://registry-1.docker.io · GitHub: https://ghcr.io · Harbor/Nexus: https://registry.example.com',
    tokenFields: ['username', 'token'],
    usernameLabel: 'Username',
    tokenLabel: 'Token or password',
    tokenPlaceholder: 'dckr_pat_… or ghp_…',
    where: [
      'Docker Hub: Account settings › Personal access tokens › Generate new token, access permissions Read-only; the username is your Docker ID.',
      'GitHub Container Registry (ghcr.io): a classic personal access token with read:packages; the username is your GitHub login.',
      'Harbor: a robot account (Project › Robot Accounts) with pull; Nexus: your user token.',
    ],
    grant: 'Flow only pulls images: read-only / pull. Push or delete rights are not needed.',
    commands: 'Docker Hub access token:\n  Read-only\n\nghcr.io, classic token scopes:\n  read:packages',
  },
  atlassian: {
    key: 'atlassian',
    label: 'Atlassian (Jira, Confluence)',
    category: 'tool',
    oauth: true,
    baseUrlRequired: true,
    baseUrlLabel: SERVER_URL,
    baseUrlPlaceholder: 'https://your-site.atlassian.net',
    baseUrlHelp: 'Your site: https://<site>.atlassian.net',
    tokenFields: ['username', 'token'],
    usernameLabel: 'Email',
    tokenLabel: 'API token',
    tokenPlaceholder: 'ATATT3x…',
    where: [
      'id.atlassian.com › Security › API tokens (https://id.atlassian.com/manage-profile/security/api-tokens).',
      'Create API token with scopes: pick Jira and Confluence, read scopes only, and an expiry.',
      'Copy it once; the username is your Atlassian account email.',
    ],
    grant: 'Read access to Jira and Confluence. Connect with OAuth asks for read:me, read:jira-work, read:confluence-content.all and offline_access.',
    commands: 'OAuth scopes:\n  read:me\n  read:jira-work\n  read:confluence-content.all\n  offline_access',
  },
  slack: {
    key: 'slack',
    label: 'Slack',
    category: 'tool',
    oauth: true,
    fixedBaseUrl: 'https://slack.com',
    defaultBaseUrl: 'https://slack.com',
    baseUrlLabel: SERVER_URL,
    tokenFields: ['token'],
    tokenPlaceholder: 'xoxb-… or xoxp-…',
    where: [
      'api.slack.com/apps › Create New App (or your workspace\'s app) › OAuth & Permissions.',
      'Bot Token Scopes: channels:read, chat:write, users:read; then Install to Workspace.',
      'Copy the Bot User OAuth Token (xoxb-…) and paste it here.',
    ],
    grant: 'Read channels and users, post messages: channels:read, chat:write, users:read. Nothing else.',
    commands: 'Bot token scopes:\n  channels:read\n  chat:write\n  users:read',
  },
};

export const TYPE_ORDER = ['github', 'gitlab', 'bitbucket', 'bitbucket-dc', 'azure-devops', 'gitea', 'npm', 'pypi', 'maven', 'oci', 'atlassian', 'slack'];

/** What an old server (no `catalog` in its answer) can store. */
export const LEGACY_TYPES = ['github', 'gitlab'];

const genericHelp = (label, category) => ({
  where: [
    `${label}: your profile or account settings › access tokens (or API tokens).`,
    'Give it the least access Flow needs and an expiry; copy it once and paste it here.',
  ],
  grant: category === 'git' ? 'Read and write access to the repositories only.' : 'Read-only access is enough.',
  commands: '',
});

/**
 * One type's definition: the catalog's TypeInfo (label, category, oauth, base URL rules, token fields, options) over
 * the local help. Works for a type only one of them knows.
 */
export function typeDef(type, info = null) {
  const local = ACCOUNT_TYPES[type] || null;
  const category = info?.category || local?.category || 'git';
  const label = info?.label || local?.label || type;
  const tokenFields = info?.tokenFields?.length ? info.tokenFields : (local?.tokenFields || ['token']);
  const options = Array.isArray(info?.options) ? info.options : (local?.options || []);
  return {
    ...genericHelp(label, category),
    baseUrlLabel: category === 'registry' ? REGISTRY_URL : SERVER_URL,
    baseUrlPlaceholder: info?.defaultBaseUrl || 'https://',
    baseUrlHelp: '',
    tokenPlaceholder: '',
    ...local,
    key: type,
    label,
    category,
    oauth: info ? Boolean(info.oauth) : Boolean(local?.oauth),
    fixedBaseUrl: info ? (info.fixedBaseUrl || null) : (local?.fixedBaseUrl || null),
    defaultBaseUrl: info?.defaultBaseUrl || local?.defaultBaseUrl || null,
    baseUrlRequired: info ? Boolean(info.baseUrlRequired) : Boolean(local?.baseUrlRequired),
    defaultScopes: info?.defaultScopes || null,
    tokenFields,
    usernameLabel: info?.usernameLabel || local?.usernameLabel || 'Username',
    options: options.filter((o) => !(local?.hiddenOptions || []).includes(o.key)),
    optionHelp: local?.optionHelp || {},
    usedFor: USED_FOR[category] || USED_FOR.tool,
    known: Boolean(local),
  };
}

/** The types to offer: the server's catalog in its order, or (old server) the git types it can store. */
export function catalogTypes(catalog) {
  if (Array.isArray(catalog) && catalog.length) { return catalog.map((info) => typeDef(info.type, info)); }
  return LEGACY_TYPES.map((type) => typeDef(type));
}

/**
 * The offers (admin apps and config providers) in one shape. An old server sends `{ alias, type, host, baseUrl }`
 * only: an enabled OAuth offer.
 */
export function normalizeOffers(providers) {
  return (providers || []).map((p) => ({
    ...p,
    id: p.id || p.appId || p.alias || `${p.type}:${p.host}`,
    appId: p.appId || null,
    alias: p.alias || null,
    source: p.source || 'config',
    name: p.name || ACCOUNT_TYPES[p.type]?.label || p.type,
    mode: p.mode || 'oauth',
    enabled: p.enabled !== false,
    options: p.options || {},
  }));
}

export const methodLabel = (method) => (method === 'oauth' ? 'OAuth' : 'token');

/** "GitHub OAuth", "npm registry token". */
export const typeLabel = (account, info = null) => `${(info?.label) || ACCOUNT_TYPES[account.type]?.label || account.type} ${methodLabel(account.method)}`;

/** Filter values: `github-oauth`, `gitlab-token`, … */
export const typeKey = (account) => `${account.type}-${account.method}`;

/** Every known type and way in (OAuth only where the type has it). */
export const TYPE_FILTERS = TYPE_ORDER.flatMap((type) => (ACCOUNT_TYPES[type].oauth ? ['oauth', 'token'] : ['token']).map((method) => ({
  value: `${type}-${method}`,
  label: `${ACCOUNT_TYPES[type].label} ${methodLabel(method)}`,
})));

/** The filters for the accounts there are, known types first in their order, then any other. */
export function typeFiltersFor(accounts, catalog = null) {
  const labels = Object.fromEntries((catalog || []).map((i) => [i.type, i.label]));
  const seen = new Map();
  (accounts || []).forEach((a) => {
    const key = typeKey(a);
    if (!seen.has(key)) { seen.set(key, { value: key, label: typeLabel(a, labels[a.type] ? { label: labels[a.type] } : null), type: a.type }); }
  });
  const rank = (t) => { const i = TYPE_ORDER.indexOf(t); return i < 0 ? TYPE_ORDER.length : i; };
  return [...seen.values()].sort((a, b) => rank(a.type) - rank(b.type) || a.label.localeCompare(b.label)).map(({ value, label }) => ({ value, label }));
}

export const categoryOf = (type, catalog = null) => (catalog || []).find((i) => i.type === type)?.category || ACCOUNT_TYPES[type]?.category || 'git';

export const STATUS = {
  ok: { color: 'green', label: 'Connected' },
  failing: { color: 'red', label: 'Test failed' },
  expiring: { color: 'orange', label: 'Expiring' },
  expired: { color: 'red', label: 'Expired' },
  untested: { color: 'default', label: 'Not tested' },
};

export const STATUS_ORDER = ['failing', 'expired', 'expiring', 'untested', 'ok'];

export const OPS = { clone: 'Cloned', fetch: 'Fetched', pull: 'Pulled', push: 'Pushed' };

/** Error codes of the accounts API, in words. */
export const ERROR_TEXT = {
  app_disabled: 'Your admin disabled this app.',
  app_not_allowed: 'Your plan or group may not use this app.',
  no_oauth: 'This app is set up for tokens only: use a token instead.',
};

export const errorText = (err) => ERROR_TEXT[err?.code] || err?.message || String(err);

/** What a search matches: name, label, host, login, username, and the projects/repositories it was used for. */
export function searchText(account) {
  const used = (account.usedBy || []).flatMap((u) => [u.projectName, u.projectId, u.repo]);
  return [account.name, account.label, account.host, account.login, account.username, account.baseUrl, ...used].filter(Boolean).join(' ').toLowerCase();
}
